import { applicationPaths } from '@core/http/applicationPaths';
import httpClient from '@core/http/httpClient';
import { accountsReducer, selectedAccStateSelector } from '@core/store/library-stores';
import { accountsThunks } from '@core/store/library-stores/accounts.thunks';
import { useAppDispatch, useAppSelector } from '@core/store/store';
import { AccountsTreeList } from '@hwm/ui-lib';
import { t } from '@lingui/macro';
import { AccountAutocompleteFilter, AccountModel } from '@shared/models';
import React, { useCallback } from 'react';

export const AccountHierarchy: React.FC<{
  /**
   * `dataTestId` - name of the Account Hierarchy (preferably `<name>-accounts-tree-list`)
   * @example
   * ```tsx
   * <AccountHierarchy dataTestId="test-accounts-tree-list" />
   */
  dataTestId: string;
}> = ({ dataTestId }) => {
  const dispatch = useAppDispatch();

  const { selectedAccount, subaccounts, hierarchy, loading } = useAppSelector(selectedAccStateSelector);

  const onAccountSelected = useCallback(
    (account: AccountModel) => {
      dispatch(accountsReducer.slice.actions.setSelectedAccount({ account: account }));
    },
    [dispatch]
  );

  const setHierarchyAction = (accounts: AccountModel[]) =>
    dispatch(accountsReducer.slice.actions.setHierarchy({ hierarchy: accounts }));

  return (
    <div className="flex flex-col h-full w-full overflow-hidden" data-testid={`${dataTestId}-wrapper`}>
      <AccountsTreeList
        dataTestId={dataTestId}
        onAccountSelected={onAccountSelected}
        selectedAccount={selectedAccount}
        subaccounts={subaccounts}
        hierarchy={hierarchy}
        setHierarchy={setHierarchyAction}
        fetchSubaccounts={(account) =>
          dispatch(
            accountsThunks.fetchSubAccounts({
              accountId: account ? (account.id || account['accountId']).toString() : null,
            })
          )
        }
        loading={loading}
        autocompleteProps={{
          placeholder: t`Search PMZ by name or description`,
          getLabel: (acc) => `${acc.name} ${acc?.description ? `(${acc?.description})` : ''}`,
          getResults: (searchQuery: string) =>
            httpClient
              .get<AccountModel[]>(applicationPaths.accounts.getAccAutocomplete, {
                params: {
                  name: searchQuery,
                  pmZoneFilter: AccountAutocompleteFilter.OnlyPmZone,
                },
              })
              .then((result) => result.data),
          optionSelected: (account) => {
            dispatch(
              accountsThunks.fetchById({
                accountId: (account?.id || account?.['accountId'])?.toString(),
              })
            );
          },
          newEditableLook: true,
          dataTestId: `${dataTestId}-autocomplete`,
        }}
      />
    </div>
  );
};
